import { useState, useCallback } from 'react';
import { X } from 'lucide-react';

const PurchaseFormModal = ({ isOpen, onClose, price }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        quantity: '1'
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showQR, setShowQR] = useState(false);
    const [error, setError] = useState('');

    const calculateTotal = useCallback(() => {
        return price * parseInt(formData.quantity);
    }, [price, formData.quantity]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        const formId = '1FAIpQLSfJzMDiCQyr7Ra4B06Rsi3z_NKbMt_Lv8uPdPWTj5cbZQMnQQ';
        const formEntries = {
            'entry.1400226767': formData.name,
            'entry.1924706870': formData.email,
            'entry.1105274418': formData.phone,
            'entry.1293834072': formData.address,
            'entry.1456431990': formData.quantity
        };

        try {
            const formUrl = `https://docs.google.com/forms/d/e/${formId}/formResponse?${new URLSearchParams(formEntries).toString()}`;

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);
            iframe.src = formUrl;

            setTimeout(() => {
                setIsSubmitting(false);
                setShowQR(true);
                document.body.removeChild(iframe);
            }, 1000);
        } catch (error) {
            console.error('Error submitting form:', error);
            setIsSubmitting(false);
            setError('An error occurred. Please try again.');
        }
    }, [formData]);

    const resetForm = useCallback(() => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            address: '',
            quantity: '1'
        });
        setShowQR(false);
        setError('');
    }, []);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 p-0 md:p-4"
            style={{
                animation: 'fadeIn 0.3s ease-out'
            }}
        >
            <div
                className="bg-white w-full md:w-[600px] max-w-[92%] md:max-w-[600px] rounded-t-[24px] md:rounded-[32px] overflow-hidden"
                style={{
                    animation: 'slideUp 0.4s ease-out'
                }}
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                    <h2 className="text-2xl font-fraunces">Purchase Calendar</h2>
                    <button
                        onClick={() => {
                            onClose();
                            resetForm();
                        }}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {!showQR ? (
                    <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6 max-h-[calc(100vh-120px)] md:max-h-[70vh] overflow-y-auto">
                        {error && (
                            <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium mb-2">Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Enter your full name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-5 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-gray-200 outline-none text-base"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Email</label>
                                <input
                                    type="email"
                                    required
                                    placeholder="Enter your email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-5 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-gray-200 outline-none text-base"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Phone Number</label>
                                <input
                                    type="tel"
                                    required
                                    placeholder="Enter your phone number"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full px-5 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-gray-200 outline-none text-base"
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Shipping Address</label>
                                <textarea
                                    required
                                    placeholder="Enter your complete shipping address"
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    className="w-full px-5 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-gray-200 outline-none text-base resize-none"
                                    rows={3}
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Quantity</label>
                                <div className="relative">
                                    <select
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                        className="w-full px-5 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-gray-200 outline-none text-base appearance-none bg-white pr-12"
                                        disabled={isSubmitting}
                                    >
                                        {[1, 2, 3, 4, 5].map(num => (
                                            <option key={num} value={num}>{num}</option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-700">
                                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 border-t mt-6">
                            <div className="flex justify-between items-center mb-6">
                                <span className="text-gray-600">Total Amount:</span>
                                <span className="text-2xl font-fraunces">₹{calculateTotal()}</span>
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full py-4 bg-black text-white rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50 text-base font-medium"
                            >
                                {isSubmitting ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        Submitting...
                                    </span>
                                ) : (
                                    'Purchase Now'
                                )}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="p-8 text-center">
                        <p className="mb-2 text-2xl font-fraunces">Thank you for your purchase!</p>
                        <p className="text-gray-600 mb-8">Please complete the payment using the QR code below</p>
                        <p className="text-xl font-medium mb-6">Total Amount: ₹{calculateTotal()}</p>
                        <div className="w-48 h-48 md:w-56 md:h-56 mx-auto mb-8 rounded-lg overflow-hidden">
                            <img
                                src="/images/QR.png"
                                alt="Payment QR Code"
                                className="w-full h-full object-contain"
                            />
                        </div>
                        <p className="text-sm text-gray-600 mb-8">After payment, you'll receive a confirmation email with delivery details</p>
                        <button
                            onClick={() => {
                                onClose();
                                resetForm();
                            }}
                            className="px-8 py-4 bg-black text-white rounded-full hover:bg-gray-800 transition-colors text-base font-medium"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>

            <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { 
            transform: translateY(100%);
            opacity: 0;
          }
          to { 
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
        </div>
    );
};

export default PurchaseFormModal;